import { TableColumns } from '@teamsnap/snap-ui';
import { ActionType } from '@teamsnap/snap-ui/build/components/ActionMenu/ActionMenu.types';
import {
  canBeAcceptedOrReleasedFromWaitlist,
  canBeDeleted,
} from 'components/registration/utils';
import { APP_URL, TEAMSNAP_URL } from 'core/constants';
import { addToArrayWhen, capitalize2 } from 'core/utils';
import { AMPLITUDE_REGISTRATION_ACTIONS } from 'hooks/amplitude';
import * as React from 'react';
import { EmailType } from '../SendEmailModal';
import StoredDocumentViewer from '../StoredDocumentViewer/StoredDocumentViewer';
import { FieldType, FormResult } from '../types';
import { FormColumn, ModalType, ParticipantListModal } from './types';

export const formatFilters = (activeFilters: object) =>
  Object.entries(activeFilters).map(([key, value]) => ({
    value,
    field: key,
    operation: 'include',
  }));

export const formatDateFields = (row: FormResult, fields: FieldType[]) => {
  // This needs to be changed as field type if needed should probably come from the column definition especially
  // since the field does not have the reporting identifier which appears to be what is used in the rows
  const dateFields = fields
    .filter((x) => x.type === 'date')
    .map((x) => `participant${x.label.replace(' ', '')}`);

  return dateFields.reduce(
    (prev, field) => ({
      ...prev,
      [field]: row[field]
        ? (`${row[field]}`.includes('/') &&
            !isNaN(new Date(row[field]) as unknown as number) &&
            new Intl.DateTimeFormat('en-US').format(new Date(row[field]))) ||
          row[field]
        : undefined,
    }),
    {}
  );
};

export const getActionsForParticipant = (
  formResult: FormResult,
  setSelectedFormResult: (x: FormResult) => void,
  setModal: (x: ParticipantListModal) => void,
  orgId: string,
  setEmailType: (x: EmailType) => void,
  handleAmplitudeAction: (action: AMPLITUDE_REGISTRATION_ACTIONS) => void
): ActionType[] => {
  const readyToRosterAction = {
    label: 'Ready to roster',
    onClick: () => {
      setModal({ isOpen: true, modalType: ModalType.ROSTER });
      setSelectedFormResult(formResult);
      handleAmplitudeAction(AMPLITUDE_REGISTRATION_ACTIONS.READY_TO_ROSTER);
    },
  };

  const viewProfileAction = {
    label: 'View Profile',
    onClick: () => {
      handleAmplitudeAction(AMPLITUDE_REGISTRATION_ACTIONS.ROSTER_PROFILE);
      window.open(
        `${TEAMSNAP_URL}/${formResult.rootDivisionId}/league_roster/player/${formResult.seasonParticipantId}`,
        '_blank'
      );
    },
  };

  const viewOrderDetailsAction = {
    label: 'Order Details',
    onClick: () => {
      handleAmplitudeAction(AMPLITUDE_REGISTRATION_ACTIONS.ORDER_DETAILS);
      window.open(
        `${APP_URL}/organizations/${orgId}/financials/order/${formResult.orderNumber}/details`,
        '_blank'
      );
    },
  };

  const sendEmailAction = {
    label: 'Email',
    onClick: () => {
      setEmailType('individual');
      setSelectedFormResult(formResult);
      setModal({ isOpen: true, modalType: ModalType.EMAIL });
      handleAmplitudeAction(AMPLITUDE_REGISTRATION_ACTIONS.EMAIL);
    },
  };

  const sendOfferAction = {
    label: 'Accept from Waitlist',
    onClick: () => {
      setModal({ isOpen: true, modalType: ModalType.OFFER });
      setSelectedFormResult(formResult);
    },
  };

  const removeWaitlistAction = {
    label: 'Release from Waitlist',
    onClick: () => {
      setModal({ isOpen: true, modalType: ModalType.REMOVE_WAITLIST });
      setSelectedFormResult(formResult);
    },
  };

  const deleteRegistrationAction = {
    label: 'Delete',
    sentiment: 'negative',
    onClick: () => {
      setModal({ isOpen: true, modalType: ModalType.DELETE_FORM_RESULT });
      setSelectedFormResult(formResult);
    },
  };

  const editRegistrationAction = {
    label: 'Edit',
    onClick: () => {
      setModal({ isOpen: true, modalType: ModalType.POST_REG_EDIT });
      setSelectedFormResult(formResult);
      handleAmplitudeAction(AMPLITUDE_REGISTRATION_ACTIONS.EDIT_REGISTRATION);
    },
  };

  const viewRegistrationEditLogsAction = {
    label: 'View Editing History',
    onClick: () => {
      setModal({ isOpen: true, modalType: ModalType.POST_REG_EDIT_LOGS });
      setSelectedFormResult(formResult);
      handleAmplitudeAction(AMPLITUDE_REGISTRATION_ACTIONS.VIEW_LOGS);
    },
  };

  const { rosterStatus, status } = formResult;
  const actions = addToArrayWhen(
    [
      [
        readyToRosterAction,
        rosterStatus === 'No Roster Profile' && status === 'Complete',
      ],
      [viewProfileAction, rosterStatus === 'Ready to Roster'],
      [
        viewOrderDetailsAction,
        !!formResult.orderNumber && formResult.orderNumber !== '--',
      ],
      [sendOfferAction, canBeAcceptedOrReleasedFromWaitlist(formResult)],
      [removeWaitlistAction, canBeAcceptedOrReleasedFromWaitlist(formResult)],
      [deleteRegistrationAction, canBeDeleted(status)],
      [
        editRegistrationAction,
        status === 'Complete' &&
          !canBeAcceptedOrReleasedFromWaitlist(formResult),
      ],
      [
        viewRegistrationEditLogsAction,
        status === 'Complete' &&
          !canBeAcceptedOrReleasedFromWaitlist(formResult),
      ],
    ],
    [sendEmailAction]
  );

  return actions;
};

export const handleStoredDocumentsForRow = (
  columns: FormColumn[],
  row: FormResult
) =>
  columns
    .filter(({ isFile }) => isFile)
    .reduce(
      (row, { name }) => ({
        ...row,
        [name]: (
          <>
            {(row[name] &&
              row[name] !== 'No File' &&
              row[name + 'Metadata'] && (
                <StoredDocumentViewer
                  internalUrl={buildStoredDocumentUrl(row[name + 'Metadata'])}
                  openInModal={true}
                >
                  {row[name]}
                </StoredDocumentViewer>
              )) ||
              'No File'}
          </>
        ),
      }),
      row
    ) || row;

const buildStoredDocumentUrl = ({ formFieldId, formResultId }): string =>
  `/registration_documents?form_field_id=${formFieldId}&form_result_id=${formResultId}`;

/**
 * Formats the columns from the API to an acceptable format for Snap-UI Table
 * @param columns Columns provided by the org API
 * @returns TableColumns (Snap-UI) formatted columns
 */
export function formatColumns(columns: FormColumn[]): TableColumns {
  const allColumns: TableColumns = {};
  const numericFields = ['formResultId', 'orderBalance'];
  columns.forEach((column: FormColumn) => {
    const { label, isSortable, name } = column;
    const width = label.length < 26 ? '187px' : `${label.length * 9}px`;
    const labelContainsDate = label.toLowerCase().includes('date');
    allColumns[name] = {
      label: capitalize2(label),
      width,
      align: 'left',
      getBodyProps: (row: FormResult) => ({
        type: 'custom',
        height: 'tall',
        children: <div className="sui-break-all">{row[name] || ''}</div>,
      }),
    };
    if (isSortable) {
      allColumns[name].sort = numericFields.includes(name)
        ? 'numeric'
        : labelContainsDate
        ? 'date'
        : 'alpha';
    }
  });
  return allColumns;
}
